<template>
  <b-modal
    id="group-users-bulk-edit-modal"
    v-model="localIsOpen"
    :title="$t('user_group_selection_option.bulk.modal_title')"
    centered
    scrollable
    no-close-on-backdrop
    no-close-on-esc
    header-bg-variant="dark"
    header-text-variant="light"
    size="xl"
    @hidden="onHidden"
  >
    <!-- SUMMARY -->
    <b-alert
      show
      variant="light"
      class="border mb-2"
    >
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <strong>
            {{ selectedCount }}
            {{ $tc('user_group_selection_option.bulk.selected_groups', selectedCount) }}
          </strong>

          <div class="text-muted small">
            {{ $t('user_group_selection_option.bulk.manage_hint') }}
          </div>
        </div>

        <feather-icon
          icon="UsersIcon"
          size="24"
        />
      </div>
    </b-alert>

    <!-- THREE COLUMNS -->
    <b-row class="users-columns mb-2">
      <!-- CURRENT USERS -->
      <b-col
        cols="12"
        lg="4"
        class="users-column-wrapper"
      >
        <div class="users-panel">
          <div class="users-panel-header">
            <div>
              <div class="users-panel-title">
                {{ $t('user_group_selection_option.bulk.current_users') }}
              </div>

              <div class="text-muted small">
                {{ $t('user_group_selection_option.bulk.currently_authorized') }}
              </div>
            </div>

            <div class="panel-header-actions">
              <b-badge variant="light-primary">
                {{ currentUsersCount }}
              </b-badge>

              <b-button
                v-if="currentUsersCount"
                variant="flat-danger"
                size="sm"
                class="panel-clear-button"
                :title="$t('user_group_selection_option.bulk.remove_all')"
                @click="removeAllCurrentUsers"
              >
                <feather-icon
                  icon="XIcon"
                  size="15"
                />
              </b-button>
            </div>
          </div>

          <div
            v-if="isLoadingCurrentUsers"
            class="users-panel-loading"
          >
            <b-spinner small />

            <span class="ml-50 text-muted small">
              {{ $t('user_group_selection_option.bulk.loading') }}
            </span>
          </div>

          <div
            v-else
            class="users-panel-list"
          >
            <template v-for="group in currentGroups">
              <div
                v-if="
                  selectedCount > 1 &&
                  getVisibleUsers(group).length
                "
                :key="`header-${group.id}`"
                class="group-divider"
              >
                <feather-icon
                  icon="UsersIcon"
                  size="12"
                  class="mr-25"
                />

                {{ group.name }}
              </div>

              <div
                v-for="user in getVisibleUsers(group)"
                :key="`${group.id}-${user.id}`"
                class="compact-user-row"
              >
                <div class="compact-avatar">
                  {{ getUserInitials(user) }}
                </div>

                <div class="compact-user-info">
                  <div class="compact-user-name">
                    {{ getUserName(user) }}
                  </div>

                  <div class="compact-user-username">
                    {{ getUsername(user) }}
                  </div>
                </div>

                <b-button
                  variant="flat-danger"
                  size="sm"
                  class="compact-action"
                  :title="$t('user_group_selection_option.bulk.remove_user')"
                  @click="queueUserRemoval(group, user)"
                >
                  <feather-icon
                    icon="XIcon"
                    size="14"
                  />
                </b-button>
              </div>
            </template>

            <div
              v-if="!currentUsersCount"
              class="panel-empty"
            >
              <feather-icon
                icon="UserXIcon"
                size="22"
                class="mb-50"
              />

              <div>
                {{ $t('user_group_selection_option.bulk.no_current_users') }}
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- USERS TO ADD -->
      <b-col
        cols="12"
        lg="4"
        class="users-column-wrapper"
      >
        <div class="users-panel">
          <div class="users-panel-header">
            <div>
              <div class="users-panel-title">
                {{ $t('user_group_selection_option.bulk.users_to_add') }}
              </div>

              <div class="text-muted small">
                {{ $t('user_group_selection_option.bulk.pending_add') }}
              </div>
            </div>

            <div class="panel-header-actions">
              <b-badge variant="light-success">
                {{ usersToAddIds.length }}
              </b-badge>

              <b-button
                v-if="usersToAddIds.length"
                variant="flat-danger"
                size="sm"
                class="panel-clear-button"
                :title="$t('user_group_selection_option.bulk.clear_add')"
                @click="clearUsersToAdd"
              >
                <feather-icon
                  icon="XIcon"
                  size="15"
                />
              </b-button>
            </div>
          </div>

          <div class="users-panel-list">
            <div
              v-for="user in usersToAdd"
              :key="`add-${getSelectedUserId(user)}`"
              class="compact-user-row"
            >
              <div class="compact-avatar success">
                {{ getUserInitials(user) }}
              </div>

              <div class="compact-user-info">
                <div class="compact-user-name">
                  {{ getUserName(user) }}
                </div>

                <div class="compact-user-username">
                  {{ getUsername(user) }}
                </div>
              </div>

              <b-button
                variant="flat-secondary"
                size="sm"
                class="compact-action"
                :title="$t('user_group_selection_option.bulk.cancel_add')"
                @click="removeUserToAdd(user)"
              >
                <feather-icon
                  icon="XIcon"
                  size="14"
                />
              </b-button>
            </div>

            <div
              v-if="!usersToAddIds.length"
              class="panel-empty"
            >
              <feather-icon
                icon="UserPlusIcon"
                size="22"
                class="mb-50"
              />

              <div>
                {{ $t('user_group_selection_option.bulk.no_users_to_add') }}
              </div>
            </div>
          </div>
        </div>
      </b-col>

      <!-- USERS TO REMOVE -->
      <b-col
        cols="12"
        lg="4"
        class="users-column-wrapper"
      >
        <div class="users-panel">
          <div class="users-panel-header">
            <div>
              <div class="users-panel-title">
                {{ $t('user_group_selection_option.bulk.users_to_remove') }}
              </div>

              <div class="text-muted small">
                {{ $t('user_group_selection_option.bulk.pending_remove') }}
              </div>
            </div>

            <div class="panel-header-actions">
              <b-badge variant="light-warning">
                {{ usersToRemove.length }}
              </b-badge>

              <b-button
                v-if="usersToRemove.length"
                variant="flat-secondary"
                size="sm"
                class="panel-clear-button"
                :title="$t('user_group_selection_option.bulk.clear_remove')"
                @click="clearUsersToRemove"
              >
                <feather-icon
                  icon="XIcon"
                  size="15"
                />
              </b-button>
            </div>
          </div>

          <div class="users-panel-list">
            <div
              v-for="item in usersToRemove"
              :key="getRemovalKey(item)"
              class="compact-user-row"
            >
              <div class="compact-avatar warning">
                {{ getUserInitials(item.user) }}
              </div>

              <div class="compact-user-info">
                <div class="compact-user-name">
                  {{ getUserName(item.user) }}
                </div>

                <div class="compact-user-username">
                  {{ getUsername(item.user) }}
                </div>

                <div
                  v-if="selectedCount > 1"
                  class="compact-group-name"
                >
                  {{ item.group_name }}
                </div>
              </div>

              <b-button
                variant="flat-secondary"
                size="sm"
                class="compact-action"
                :title="$t('user_group_selection_option.bulk.undo')"
                @click="undoUserRemoval(item)"
              >
                <feather-icon
                  icon="RotateCcwIcon"
                  size="14"
                />
              </b-button>
            </div>

            <div
              v-if="!usersToRemove.length"
              class="panel-empty"
            >
              <feather-icon
                icon="UserMinusIcon"
                size="22"
                class="mb-50"
              />

              <div>
                {{ $t('user_group_selection_option.bulk.no_users_to_remove') }}
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>

    <!-- MANAGEMENT -->
    <div class="management-section">
      <!-- USERS -->
      <div class="management-block">
        <div class="management-title">
          <feather-icon
            icon="UserIcon"
            size="16"
            class="mr-50"
          />

          {{ $t('user_group_selection_option.bulk.users') }}
        </div>

        <div class="text-muted small mb-1">
          {{ $t('user_group_selection_option.bulk.search_users_hint') }}
        </div>

        <users-select-paginated
          v-model="searchUsers"
          multiple
          endpoint="/v1/users"
          label-key="full_name"
          :show-email-in-label="true"
          :disabled="
            isSubmitting ||
            isLoadingProfileUsers
          "
        />

        <div
          v-if="searchUserIds.length"
          class="search-selection-info mt-1"
        >
          <div>
            <strong>
              {{ searchUserIds.length }}
            </strong>

            {{ $tc('user_group_selection_option.bulk.selected_users', searchUserIds.length) }}
          </div>

          <b-button
            variant="flat-secondary"
            size="sm"
            @click="clearSearchUsers"
          >
            {{ $t('user_group_selection_option.bulk.clear') }}
          </b-button>
        </div>

        <div class="user-action-buttons">
          <b-button
            variant="success"
            :disabled="
              isSubmitting ||
              !searchUserIds.length
            "
            @click="addSearchUsers"
          >
            <feather-icon
              icon="UserPlusIcon"
              size="16"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.add_selected') }}
          </b-button>

          <b-button
            variant="warning"
            :disabled="
              isSubmitting ||
              !searchUserIds.length
            "
            @click="removeSearchUsers"
          >
            <feather-icon
              icon="UserMinusIcon"
              size="16"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.remove_selected') }}
          </b-button>
        </div>
      </div>

      <!-- PROFILE -->
      <div class="management-block">
        <div class="management-title">
          <feather-icon
            icon="FilterIcon"
            size="16"
            class="mr-50"
          />

          {{ $t('user_group_selection_option.bulk.profile') }}
        </div>

        <div class="text-muted small mb-1">
          {{ $t('user_group_selection_option.bulk.profile_hint') }}
        </div>

        <v-select
          v-model="profileFilter"
          :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
          :options="profileOptions"
          :reduce="(profile) => profile.value"
          :clearable="true"
          :placeholder="$t('user_group_selection_option.bulk.select_profile')"
        />

        <div
          v-if="profileFilter"
          class="selected-profile-info mt-1"
        >
          {{ $t('user_group_selection_option.bulk.selected_profile') }}
          <strong>
            {{ selectedProfileLabel }}
          </strong>

          <div class="text-muted mt-25">
            <template v-if="selectedCount === 1">
              {{ $t('user_group_selection_option.bulk.remove_profile_single_hint') }}
            </template>

            <template v-else>
              {{ $t('user_group_selection_option.bulk.remove_profile_multiple_hint') }}
            </template>
          </div>
        </div>

        <div class="profile-action-grid">
          <b-button
            variant="outline-success"
            :disabled="
              !profileFilter ||
              isSubmitting ||
              isLoadingProfileUsers
            "
            @click="addProfileToAdd"
          >
            <b-spinner
              v-if="
                profileAction ===
                'add-profile-to-add'
              "
              small
              class="mr-50"
            />

            <feather-icon
              v-else
              icon="UserPlusIcon"
              size="15"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.profile_add_to_add') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            :disabled="
              !profileFilter ||
              !usersToAddIds.length ||
              isSubmitting ||
              isLoadingProfileUsers
            "
            @click="removeProfileFromAdd"
          >
            <b-spinner
              v-if="
                profileAction ===
                'remove-profile-from-add'
              "
              small
              class="mr-50"
            />

            <feather-icon
              v-else
              icon="XIcon"
              size="15"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.profile_remove_from_add') }}
          </b-button>

          <b-button
            variant="outline-warning"
            :disabled="
              !profileFilter ||
              isSubmitting ||
              isLoadingProfileUsers
            "
            @click="addProfileToRemove"
          >
            <b-spinner
              v-if="
                profileAction ===
                'add-profile-to-remove'
              "
              small
              class="mr-50"
            />

            <feather-icon
              v-else
              icon="UserMinusIcon"
              size="15"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.profile_add_to_remove') }}
          </b-button>

          <b-button
            variant="outline-secondary"
            :disabled="
              !profileFilter ||
              !usersToRemove.length ||
              isSubmitting ||
              isLoadingProfileUsers
            "
            @click="removeProfileFromRemove"
          >
            <b-spinner
              v-if="
                profileAction ===
                'remove-profile-from-remove'
              "
              small
              class="mr-50"
            />

            <feather-icon
              v-else
              icon="RotateCcwIcon"
              size="15"
              class="mr-50"
            />

            {{ $t('user_group_selection_option.bulk.profile_remove_from_remove') }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- SUMMARY -->
    <b-alert
      v-if="hasChanges"
      show
      variant="light-primary"
      class="mt-2 mb-0"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="InfoIcon"
          size="17"
          class="mr-1"
        />

        <span>
          <template v-if="usersToAddIds.length">
            {{ $tc('user_group_selection_option.bulk.add_summary', usersToAddIds.length, { groups: $tc('user_group_selection_option.bulk.group_count', selectedCount) }) }}
          </template>

          <template
            v-if="
              usersToAddIds.length &&
              usersToRemove.length
            "
          >
            &nbsp;
          </template>

          <template v-if="usersToRemove.length">
            {{ $tc('user_group_selection_option.bulk.remove_summary', usersToRemove.length) }}
          </template>
        </span>
      </div>
    </b-alert>

    <!-- FOOTER -->
    <template #modal-footer>
      <b-button
        variant="secondary"
        :disabled="isSubmitting"
        @click="close"
      >
        {{ $t("actions.cancel") }}
      </b-button>

      <b-button
        variant="primary"
        :disabled="
          isSubmitting ||
          !selectedCount ||
          !hasChanges
        "
        @click="submit"
      >
        <b-spinner
          v-if="isSubmitting"
          small
          class="mr-50"
        />

        {{ $t('user_group_selection_option.bulk.apply_changes') }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import i18n from "@/libs/i18n";
import {
  BAlert,
  BBadge,
  BButton,
  BCol,
  BRow,
  BSpinner,
} from "bootstrap-vue";
import {
  computed,
  ref,
  watch,
} from "@vue/composition-api";
import axios from "@axios";
import vSelect from "vue-select";
import store from "@/store";
import UsersSelectPaginated from "@/components/UsersSelectPaginated.vue";

export default {
  name: "GroupUsersBulkEditModal",

  components: {
    BAlert,
    BBadge,
    BButton,
    BCol,
    BRow,
    BSpinner,
    vSelect,
    UsersSelectPaginated,
  },

  props: {
    isOpen: {
      type: Boolean,
      required: true,
    },

    groupIds: {
      type: Array,
      default: () => [],
    },

    selectedCount: {
      type: Number,
      default: 0,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },

  setup(props, { emit }) {
    const currentGroups = ref([]);
    const usersToAdd = ref([]);
    const usersToRemove = ref([]);
    const searchUsers = ref([]);
    const profileFilter = ref(null);

    const isLoadingCurrentUsers = ref(false);
    const isLoadingProfileUsers = ref(false);
    const profileAction = ref(null);

    const localIsOpen = computed({
      get() {
        return props.isOpen;
      },

      set(value) {
        if (!value) {
          emit("close");
        }
      },
    });

    const profileOptions = computed(() => {
      return store.getters["profile/getItems"].map(
        (profile) => ({
          ...profile,
          value: profile.id,
          label: profile.type,
        })
      );
    });

    const selectedProfileLabel = computed(() => {
      const profile =
        profileOptions.value.find(
          (item) =>
            Number(item.value) ===
            Number(profileFilter.value)
        );

      return profile?.label ?? "";
    });

    const getSelectedUserId = (user) => {
      if (
        user !== null &&
        typeof user === "object"
      ) {
        return (
          user.id ??
          user.value ??
          null
        );
      }

      return user;
    };

    const normalizeUserId = (user) => {
      const id =
        getSelectedUserId(user);

      if (
        id === null ||
        id === undefined
      ) {
        return null;
      }

      const numberId =
        Number(id);

      return Number.isNaN(numberId)
        ? null
        : numberId;
    };

    const searchUserIds = computed(() => {
      if (
        !Array.isArray(
          searchUsers.value
        )
      ) {
        return [];
      }

      return searchUsers.value
        .map(normalizeUserId)
        .filter(
          (id) =>
            id !== null
        );
    });

    const usersToAddIds = computed(() => {
      if (
        !Array.isArray(
          usersToAdd.value
        )
      ) {
        return [];
      }

      return usersToAdd.value
        .map(normalizeUserId)
        .filter(
          (id) =>
            id !== null
        );
    });

    const hasChanges = computed(() => {
      return (
        usersToAddIds.value.length > 0 ||
        usersToRemove.value.length > 0
      );
    });

    const getUserName = (user) => {
      return (
        user?.full_name ??
        user?.name ??
        user?.label ??
        i18n.t("user_group_selection_option.bulk.user_fallback", { id: getSelectedUserId(user) ?? "" })
      );
    };

    /**
     * Usuario/login primero.
     * Email sólo como fallback.
     */
    const getUsername = (user) => {
      return (
        user?.username ??
        user?.user ??
        user?.user_name ??
        user?.login ??
        user?.email ??
        ""
      );
    };

    const getUserInitials = (user) => {
      const name =
        getUserName(user);

      if (!name) {
        return "?";
      }

      const parts = name
        .trim()
        .split(/\s+/)
        .filter(Boolean);

      if (!parts.length) {
        return "?";
      }

      if (parts.length === 1) {
        return parts[0]
          .substring(0, 2)
          .toUpperCase();
      }

      return (
        parts[0][0] +
        parts[
          parts.length - 1
        ][0]
      ).toUpperCase();
    };

    const getRemovalKey = (item) => {
      return `${
        item.group_id === null ||
        item.group_id === undefined
          ? "all"
          : item.group_id
      }-${item.user.id}`;
    };

    const isQueuedForRemoval = (
      groupId,
      userId
    ) => {
      return usersToRemove.value.some(
        (item) => {
          if (
            Number(item.user.id) !==
            Number(userId)
          ) {
            return false;
          }

          if (
            item.group_id === null ||
            item.group_id === undefined
          ) {
            return true;
          }

          return (
            Number(item.group_id) ===
            Number(groupId)
          );
        }
      );
    };

    const getVisibleUsers = (group) => {
      if (
        !group ||
        !Array.isArray(group.users)
      ) {
        return [];
      }

      return group.users.filter(
        (user) =>
          !isQueuedForRemoval(
            group.id,
            user.id
          )
      );
    };

    const currentUsersCount =
      computed(() => {
        return currentGroups.value.reduce(
          (total, group) => {
            return (
              total +
              getVisibleUsers(
                group
              ).length
            );
          },
          0
        );
      });

    const removeUserToAdd = (user) => {
      const userId =
        normalizeUserId(user);

      usersToAdd.value =
        usersToAdd.value.filter(
          (currentUser) =>
            normalizeUserId(
              currentUser
            ) !==
            userId
        );
    };

    const clearUsersToAdd = () => {
      usersToAdd.value = [];
    };

    const queueUserRemoval = (
      group,
      user
    ) => {
      if (
        isQueuedForRemoval(
          group.id,
          user.id
        )
      ) {
        return;
      }

      removeUserToAdd(user);

      usersToRemove.value = [
        ...usersToRemove.value,
        {
          group_id:
            group.id,

          group_name:
            group.name,

          user,
        },
      ];
    };

    /**
     * Quita todos los usuarios que todavía
     * aparecen como actuales.
     *
     * Se agregan como removals específicos
     * por grupo.
     */
    const removeAllCurrentUsers = () => {
      currentGroups.value.forEach(
        (group) => {
          const visibleUsers =
            getVisibleUsers(group);

          visibleUsers.forEach(
            (user) => {
              queueUserRemoval(
                group,
                user
              );
            }
          );
        }
      );
    };

    const undoUserRemoval = (item) => {
      usersToRemove.value =
        usersToRemove.value.filter(
          (current) => {
            const sameUser =
              Number(
                current.user.id
              ) ===
              Number(
                item.user.id
              );

            const sameGroup =
              current.group_id ===
                item.group_id ||
              (
                current.group_id === null &&
                item.group_id === null
              );

            return !(
              sameUser &&
              sameGroup
            );
          }
        );
    };

    /**
     * Al vaciar "usuarios a quitar"
     * simplemente cancelamos todas las
     * eliminaciones pendientes.
     *
     * Como getVisibleUsers() depende de esta
     * lista, vuelven a aparecer automáticamente
     * en "Usuarios en grupos".
     */
    const clearUsersToRemove = () => {
      usersToRemove.value = [];
    };

    const addUsersToPending = (users) => {
      if (
        !Array.isArray(users)
      ) {
        return;
      }

      const existingIds =
        new Set(
          usersToAddIds.value
        );

      const newUsers = [
        ...usersToAdd.value,
      ];

      users.forEach((user) => {
        const userId =
          normalizeUserId(user);

        if (
          userId === null ||
          existingIds.has(
            userId
          )
        ) {
          return;
        }

        /**
         * No puede existir simultáneamente
         * en agregar y quitar.
         */
        usersToRemove.value =
          usersToRemove.value.filter(
            (item) =>
              Number(
                item.user.id
              ) !==
              Number(userId)
          );

        newUsers.push(user);

        existingIds.add(
          userId
        );
      });

      usersToAdd.value =
        newUsers;
    };

    const addUsersToRemoveMassive = (
      users
    ) => {
      if (
        !Array.isArray(users)
      ) {
        return;
      }

      const existingIds =
        new Set(
          usersToRemove.value.map(
            (item) =>
              Number(
                item.user.id
              )
          )
        );

      const newRemovals = [
        ...usersToRemove.value,
      ];

      users.forEach((user) => {
        const userId =
          normalizeUserId(user);

        if (
          userId === null ||
          existingIds.has(
            userId
          )
        ) {
          return;
        }

        usersToAdd.value =
          usersToAdd.value.filter(
            (currentUser) =>
              normalizeUserId(
                currentUser
              ) !==
              userId
          );

        newRemovals.push({
          group_id:
            null,

          group_name:
            i18n.t("user_group_selection_option.bulk.all_selected_groups"),

          user,
        });

        existingIds.add(
          userId
        );
      });

      usersToRemove.value =
        newRemovals;
    };

    const addUsersToRemoveSingleGroup = (
      profileUserIds
    ) => {
      const selectedIds =
        new Set(
          profileUserIds.map(
            (id) =>
              Number(id)
          )
        );

      const group =
        currentGroups.value[0];

      if (
        !group ||
        !Array.isArray(
          group.users
        )
      ) {
        return;
      }

      group.users.forEach(
        (user) => {
          const userId =
            Number(
              user.id
            );

          if (
            !selectedIds.has(
              userId
            )
          ) {
            return;
          }

          if (
            isQueuedForRemoval(
              group.id,
              userId
            )
          ) {
            return;
          }

          removeUserToAdd(
            user
          );

          usersToRemove.value = [
            ...usersToRemove.value,
            {
              group_id:
                group.id,

              group_name:
                group.name,

              user,
            },
          ];
        }
      );
    };

    const addSearchUsers = () => {
      addUsersToPending(
        searchUsers.value
      );

      searchUsers.value = [];
    };

    const removeSearchUsers = () => {
      if (
        !searchUsers.value.length
      ) {
        return;
      }

      if (
        props.selectedCount === 1
      ) {
        addUsersToRemoveSingleGroup(
          searchUserIds.value
        );
      } else {
        addUsersToRemoveMassive(
          searchUsers.value
        );
      }

      searchUsers.value = [];
    };

    const clearSearchUsers = () => {
      searchUsers.value = [];
    };

    const normalizeUsersResponse = (
      response
    ) => {
      const payload =
        response?.data ??
        response;

      if (
        Array.isArray(
          payload
        )
      ) {
        return {
          items:
            payload,

          total:
            payload.length,
        };
      }

      if (
        payload &&
        Array.isArray(
          payload.data
        )
      ) {
        return {
          items:
            payload.data,

          total:
            payload.meta?.total ??
            payload.total ??
            payload.data.length,
        };
      }

      if (
        payload?.data &&
        Array.isArray(
          payload.data.data
        )
      ) {
        return {
          items:
            payload.data.data,

          total:
            payload.data.meta?.total ??
            payload.data.total ??
            payload.data.data.length,
        };
      }

      return {
        items: [],
        total: 0,
      };
    };

    const getAllUsersFromProfile =
      async () => {
        if (
          !profileFilter.value
        ) {
          return [];
        }

        const pageSize = 100;

        let page = 1;
        let users = [];
        let total = null;

        while (
          total === null ||
          users.length < total
        ) {
          const response =
            await axios.get(
              "/v1/users",
              {
                params: {
                  id_profile:
                    profileFilter.value,

                  max:
                    pageSize,

                  "page[n]":
                    page,
                },
              }
            );

          const normalized =
            normalizeUsersResponse(
              response
            );

          if (
            !normalized.items.length
          ) {
            break;
          }

          users = [
            ...users,
            ...normalized.items,
          ];

          total =
            Number(
              normalized.total
            );

          if (
            !total ||
            normalized.items.length <
              pageSize
          ) {
            break;
          }

          page += 1;
        }

        return users.filter(
          (
            user,
            index,
            array
          ) => {
            const userId =
              normalizeUserId(
                user
              );

            return (
              array.findIndex(
                (item) =>
                  normalizeUserId(
                    item
                  ) ===
                  userId
              ) ===
              index
            );
          }
        );
      };

    const getProfileUsers =
      async () => {
        const users =
          await getAllUsersFromProfile();

        const ids =
          users
            .map(
              normalizeUserId
            )
            .filter(
              (id) =>
                id !== null
            );

        return {
          users,
          ids,
        };
      };

    const addProfileToAdd =
      async () => {
        if (
          !profileFilter.value ||
          isLoadingProfileUsers.value
        ) {
          return;
        }

        isLoadingProfileUsers.value =
          true;

        profileAction.value =
          "add-profile-to-add";

        try {
          const {
            users,
          } =
            await getProfileUsers();

          addUsersToPending(
            users
          );
        } catch (error) {
          console.error(
            'Error poniendo perfil en "usuarios a agregar":',
            error
          );
        } finally {
          isLoadingProfileUsers.value =
            false;

          profileAction.value =
            null;
        }
      };

    const removeProfileFromAdd =
      async () => {
        if (
          !profileFilter.value ||
          isLoadingProfileUsers.value
        ) {
          return;
        }

        isLoadingProfileUsers.value =
          true;

        profileAction.value =
          "remove-profile-from-add";

        try {
          const {
            ids,
          } =
            await getProfileUsers();

          const profileIds =
            new Set(
              ids.map(
                (id) =>
                  Number(id)
              )
            );

          usersToAdd.value =
            usersToAdd.value.filter(
              (user) =>
                !profileIds.has(
                  normalizeUserId(
                    user
                  )
                )
            );
        } catch (error) {
          console.error(
            'Error eliminando perfil de "usuarios a agregar":',
            error
          );
        } finally {
          isLoadingProfileUsers.value =
            false;

          profileAction.value =
            null;
        }
      };

    const addProfileToRemove =
      async () => {
        if (
          !profileFilter.value ||
          isLoadingProfileUsers.value
        ) {
          return;
        }

        isLoadingProfileUsers.value =
          true;

        profileAction.value =
          "add-profile-to-remove";

        try {
          const {
            users,
            ids,
          } =
            await getProfileUsers();

          if (
            props.selectedCount === 1
          ) {
            addUsersToRemoveSingleGroup(
              ids
            );
          } else {
            addUsersToRemoveMassive(
              users
            );
          }
        } catch (error) {
          console.error(
            'Error poniendo perfil en "usuarios a quitar":',
            error
          );
        } finally {
          isLoadingProfileUsers.value =
            false;

          profileAction.value =
            null;
        }
      };

    const removeProfileFromRemove =
      async () => {
        if (
          !profileFilter.value ||
          isLoadingProfileUsers.value
        ) {
          return;
        }

        isLoadingProfileUsers.value =
          true;

        profileAction.value =
          "remove-profile-from-remove";

        try {
          const {
            ids,
          } =
            await getProfileUsers();

          const profileIds =
            new Set(
              ids.map(
                (id) =>
                  Number(id)
              )
            );

          usersToRemove.value =
            usersToRemove.value.filter(
              (item) =>
                !profileIds.has(
                  Number(
                    item.user.id
                  )
                )
            );
        } catch (error) {
          console.error(
            'Error eliminando perfil de "usuarios a quitar":',
            error
          );
        } finally {
          isLoadingProfileUsers.value =
            false;

          profileAction.value =
            null;
        }
      };

    const loadCurrentUsers =
      async () => {
        if (
          !props.isOpen ||
          !props.groupIds.length
        ) {
          currentGroups.value = [];

          return;
        }

        isLoadingCurrentUsers.value =
          true;

        try {
          const response =
            await store.dispatch(
              "user_group_selection_option/getGroupUsers",
              {
                group_ids:
                  props.groupIds,

                limit:
                  100,

                offset:
                  0,
              }
            );

          currentGroups.value =
            response?.data
              ?.groups ??
            response?.groups ??
            [];
        } catch (error) {
          console.error(
            "Error loading current group users:",
            error
          );

          currentGroups.value =
            [];
        } finally {
          isLoadingCurrentUsers.value =
            false;
        }
      };

    const loadProfiles = () => {
      store.dispatch(
        "profile/getAll"
      );
    };

    watch(
      () => [
        props.isOpen,
        props.groupIds.join(
          ","
        ),
      ],

      () => {
        if (
          props.isOpen
        ) {
          loadProfiles();
          loadCurrentUsers();
        }
      }
    );

    const close = () => {
      emit("close");
    };

    const buildRemovals = () => {
      const removals = [];

      usersToRemove.value.forEach(
        (item) => {
          const userId =
            Number(
              item.user.id
            );

          if (
            item.group_id !== null &&
            item.group_id !== undefined
          ) {
            removals.push({
              group_id:
                Number(
                  item.group_id
                ),

              user_id:
                userId,
            });

            return;
          }

          props.groupIds.forEach(
            (groupId) => {
              removals.push({
                group_id:
                  Number(
                    groupId
                  ),

                user_id:
                  userId,
              });
            }
          );
        }
      );

      return removals.filter(
        (
          item,
          index,
          array
        ) =>
          array.findIndex(
            (current) =>
              Number(
                current.group_id
              ) ===
                Number(
                  item.group_id
                ) &&
              Number(
                current.user_id
              ) ===
                Number(
                  item.user_id
                )
          ) ===
          index
      );
    };

    const submit = () => {
      if (
        !props.selectedCount ||
        !hasChanges.value
      ) {
        return;
      }

      emit("submit", {
        add_user_ids:
          usersToAddIds.value,

        removals:
          buildRemovals(),
      });
    };

    const reset = () => {
      currentGroups.value = [];
      usersToAdd.value = [];
      usersToRemove.value = [];
      searchUsers.value = [];
      profileFilter.value = null;
      profileAction.value = null;
      isLoadingProfileUsers.value = false;
    };

    const onHidden = () => {
      reset();

      emit("close");
    };

    return {
      localIsOpen,

      currentGroups,
      currentUsersCount,
      isLoadingCurrentUsers,

      usersToAdd,
      usersToAddIds,
      usersToRemove,

      searchUsers,
      searchUserIds,

      profileFilter,
      profileOptions,
      selectedProfileLabel,

      isLoadingProfileUsers,
      profileAction,

      hasChanges,

      getSelectedUserId,
      getUserName,
      getUsername,
      getUserInitials,
      getRemovalKey,

      getVisibleUsers,

      queueUserRemoval,
      removeAllCurrentUsers,
      undoUserRemoval,
      clearUsersToAdd,
      clearUsersToRemove,
      removeUserToAdd,

      addSearchUsers,
      removeSearchUsers,
      clearSearchUsers,

      addProfileToAdd,
      removeProfileFromAdd,
      addProfileToRemove,
      removeProfileFromRemove,

      close,
      submit,
      onHidden,
    };
  },
};
</script>

<style lang="scss" scoped>
.users-columns {
  margin-left: -0.4rem;
  margin-right: -0.4rem;
}

.users-column-wrapper {
  padding-left: 0.4rem;
  padding-right: 0.4rem;
}

.users-panel {
  height: 310px;
  display: flex;
  flex-direction: column;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fafafa;
  overflow: hidden;
}

.users-panel-header {
  min-height: 67px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.8rem 0.9rem;
  border-bottom: 1px solid #ebe9f1;
  background: #fff;
}

.users-panel-title {
  color: #5e5873;
  font-size: 0.9rem;
  font-weight: 600;
}

.panel-header-actions {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.panel-clear-button {
  width: 28px;
  height: 28px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.users-panel-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.users-panel-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.group-divider {
  display: flex;
  align-items: center;
  padding: 0.35rem 0.65rem;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.05);
  border-bottom: 1px solid #ebe9f1;
  font-size: 0.7rem;
  font-weight: 600;
}

.compact-user-row {
  min-height: 52px;
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr) 32px;
  align-items: center;
  gap: 0.55rem;
  padding: 0.45rem 0.65rem;
  border-bottom: 1px solid #ebe9f1;
  background: #fff;

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgba(115, 103, 240, 0.025);
  }
}

.compact-avatar {
  width: 31px;
  height: 31px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: #7367f0;
  background: rgba(115, 103, 240, 0.1);
  font-size: 0.64rem;
  font-weight: 600;

  &.success {
    color: #28c76f;
    background: rgba(40, 199, 111, 0.1);
  }

  &.warning {
    color: #ff9f43;
    background: rgba(255, 159, 67, 0.12);
  }
}

.compact-user-info {
  min-width: 0;
}

.compact-user-name {
  overflow: hidden;
  color: #5e5873;
  font-size: 0.8rem;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-user-username {
  margin-top: 0.05rem;
  overflow: hidden;
  color: #82808b;
  font-size: 0.7rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-group-name {
  margin-top: 0.05rem;
  overflow: hidden;
  color: #7367f0;
  font-size: 0.65rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.compact-action {
  width: 30px;
  height: 30px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.panel-empty {
  height: 100%;
  min-height: 150px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #a6a4b0;
  font-size: 0.75rem;
}

.management-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.management-block {
  padding: 1rem;
  border: 1px solid #ebe9f1;
  border-radius: 0.5rem;
  background: #fafafa;
}

.management-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
  color: #5e5873;
  font-size: 0.9rem;
  font-weight: 600;
}

.search-selection-info {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 38px;
  padding: 0.4rem 0.65rem;
  border-radius: 0.428rem;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.05);
  font-size: 0.78rem;
}

.user-action-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 1rem;
}

.selected-profile-info {
  padding: 0.45rem 0.65rem;
  border-radius: 0.428rem;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.05);
  font-size: 0.78rem;
}

.profile-action-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
  margin-top: 1rem;
}

.profile-action-grid .btn {
  min-height: 42px;
  white-space: normal;
}

@media (max-width: 991px) {
  .users-column-wrapper {
    margin-bottom: 1rem;
  }

  .users-panel {
    height: 260px;
  }

  .management-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .user-action-buttons {
    flex-direction: column;
  }

  .user-action-buttons .btn {
    width: 100%;
  }

  .profile-action-grid {
    grid-template-columns: 1fr;
  }
}
</style>
